import { TSource } from '@core/interfaces/Endpoints';

export type SourceUpdateBody = Partial<TSource>;

export type TConfluencePaging = {
  limit: number;
  size: number;
  start: number;
  _links?: {
    next: string;
    prev: string;
  };
};

export const INITIAL_CONFLUENCE_PAGING: TConfluencePaging = {
  limit: 20,
  size: 20,
  start: 0,
};

export type TConfluencePaginated<T extends TConfluenceSpace | TConfluencePage> = {
  results: T[];
} & TConfluencePaging;

export type TConfluenceSpace = {
  name: string;
  key: string;
};

export type TConfluencePage = {
  id: string;
  status: string;
  title: string;
  space?: string;
  spaceKey?: string;
};

export type TConfluenceImport = {
  folderName: string;
  folderStatus: string;
  pagesIds: string[];
  spacesIds: string[];
};

export interface TExternalReference {
  referenceText: string;
  referenceLink: string;
  _id?: string;
}

export interface TSourceEmbedding {
  externalReferences: any[];
  /** Optional; some pipelines may also expose citation IDs on the chunk. */
  citationIds?: string[];
  _id: string;
  content: string;
  source: string[];
  priority: number;
  status: string;
  publicationDate: Date | null;
  countries: any[];
  __v: number;
  expirationDate?: Date;
  publicationYear?: number;
  publicationMonth?: number;
  dateRangeStart?: Date;
  dateRangeEnd?: Date;
  keywords?: string[];
  summary?: string;
}

// Veeva Annotations Types
export interface VeevaPlacemark {
  y_coordinate__sys?: number;
  coordinates__sys?: number[];
  text_start_index__sys?: number;
  text_end_index__sys?: number;
  type__sys: string;
  height__sys?: number;
  width__sys?: number;
  x_coordinate__sys?: number;
  page_number__sys?: number;
  style__sys?: string;
}

export interface VeevaAnnotation {
  document_version_id__sys: string;
  modified_by_user__sys: string;
  references: any[];
  id__sys: string;
  placemark?: VeevaPlacemark;
  created_by_user__sys: string;
  comment__sys: string;
  created_date_time__sys: string;
  modified_date_time__sys: string;
  persistent_id__sys: string;
  prevent_bring_forward__sys: boolean;
  title__sys: string;
  type__sys: string;
  state__sys: string;
  reply_count__sys: number;
  color__sys: string;
  external_id__sys?: string;
}

export interface VeevaAnnotationsResponse {
  success: boolean;
  sourceId: string;
  sourceName: string;
  veevaDocumentId: string;
  documentId: string;
  documentVersionId: string;
  annotationCount: number;
  annotations: VeevaAnnotation[];
  pagination: {
    total: number;
    size: number;
    offset: number;
    limit: number;
  };
  message: string;
}

export interface VeevaNewAnnotation {
  type: string;
  text: string;
  suggestion: string;
  context: string;
  reason: string;
  startOffset: number;
  endOffset: number;
  chunkIndex: number;
}

export interface VeevaAnnotationTypes {
  spelling: number;
  grammar: number;
  punctuation: number;
  style: number;
  consistency: number;
}

export interface VeevaAnnotateResponse {
  success: boolean;
  sourceId: string;
  veevaDocumentId: string;
  documentName: string;
  contentLength: number;
  chunksProcessed: number;
  annotationsFound: number;
  annotationsSaved: number;
  annotationsFailed: number;
  annotations: VeevaNewAnnotation[];
  saveErrors: any[];
  annotationTypes: VeevaAnnotationTypes;
  message: string;
  mongodb?: {
    saved: number;
    coordinatesResolved: number;
    coordinatesMissed: number;
  };
}

// ---------------------------------------------------------------------------
// Our own annotation system (MongoDB-persisted, with PDF coordinates)
// ---------------------------------------------------------------------------

export interface AnnotationRect {
  x: number;
  y: number;
  width: number;
  height: number;
  pageWidth?: number;
  pageHeight?: number;
  coordinateOrigin?: 'top-left' | 'bottom-left';
}

export type AnnotationOrigin = 'ai' | 'manual' | 'veeva';
export type AnnotationStatus = 'open' | 'resolved' | 'rejected';
export type PdfSelectionMode = 'none' | 'text' | 'area';

export interface PdfAnnotation {
  _id: string;
  sourceId: string;
  projectId?: string;
  userId?: string;
  pageNumber: number;
  rect: AnnotationRect;
  rects?: AnnotationRect[];
  textContent: string;
  type: string;
  comment: string;
  suggestion?: string;
  color: string;
  origin: AnnotationOrigin;
  status: AnnotationStatus;
  confidence?: number;
  verificationStatus?: string | null;
  chunkId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PdfAnnotationDraft {
  pageNumber: number;
  rect: AnnotationRect;
  rects?: AnnotationRect[];
  textContent: string;
  type: string;
  comment: string;
  suggestion?: string;
  color: string;
  origin: 'manual';
  selectionMode: Exclude<PdfSelectionMode, 'none'>;
}

export interface PdfAnnotationsListResponse {
  success: boolean;
  sourceId: string;
  count: number;
  annotations: PdfAnnotation[];
}

export interface PdfAnnotationGenerateResponse {
  success: boolean;
  sourceId: string;
  annotationsFound: number;
  annotationsSaved: number;
  coordinatesResolved: number;
  coordinatesMissed: number;
  annotations: PdfAnnotation[];
}

export interface CreatePdfAnnotationDto {
  sourceId: string;
  pageNumber: number;
  rect: AnnotationRect;
  rects?: AnnotationRect[];
  textContent: string;
  type: string;
  comment: string;
  suggestion?: string;
  color?: string;
  origin?: AnnotationOrigin;
}

export interface UpdatePdfAnnotationDto {
  comment?: string;
  suggestion?: string;
  status?: AnnotationStatus;
  color?: string;
}
